import { css, html } from "lit";
import { query, state } from "lit/decorators.js";
import { when } from "lit-html/directives/when.js";
import "../Tooltip/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { getThumbClasses } from "./utils.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_STEP = 1;
const DEFAULT_VALUE = [0, 0];
const TRACK_PADDING = 8;
let DsfRangeSlider = class extends BaseComponent {
  constructor() {
    super();
    this.value = DEFAULT_VALUE;
    this.dsf_mode = "single";
    this.dsf_min = DEFAULT_MIN;
    this.dsf_max = DEFAULT_MAX;
    this.dsf_step = DEFAULT_STEP;
    this._startValue = 0;
    this._endValue = 0;
    this._isDragging = false;
    this._dragStartX = 0;
    this.supportedKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
    this.handleDocumentMouseMove = this.handleDocumentMouseMove.bind(this);
    this.handleDocumentMouseUp = this.handleDocumentMouseUp.bind(this);
    this.handleDocumentMouseLeave = this.handleDocumentMouseLeave.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.addEventListener("keydown", this.handleKeyDown);
  }
  get rangeMin() {
    return this.dsf_min < this.dsf_max ? this.dsf_min : DEFAULT_MIN;
  }
  get rangeMax() {
    return this.dsf_max > this.dsf_min ? this.dsf_max : DEFAULT_MAX;
  }
  get step() {
    const valueRange = this.rangeMax - this.rangeMin;
    return valueRange % this.dsf_step === 0 ? this.dsf_step : DEFAULT_STEP;
  }
  get scale() {
    var _a;
    const valueRange = this.rangeMax - this.rangeMin;
    const trackWidth = ((_a = this._rangeTrack) == null ? void 0 : _a.clientWidth) || 0;
    return trackWidth / valueRange;
  }
  firstUpdated(changedProps) {
    if (changedProps.has("value") && this.value === DEFAULT_VALUE && this.dsf_mode === "range") {
      this.value = [this.rangeMin, this.rangeMin + this.step];
    }
  }
  update(changedProps) {
    if (changedProps.has("value")) {
      this._startValue = this.value[0];
      this._endValue = this.value[1];
    }
    super.update(changedProps);
  }
  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
    this._observer = new ResizeObserver(() => {
      this.requestUpdate();
    });
    this._observer.observe(this._rangeTrack);
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this._observer) == null ? void 0 : _a.disconnect();
    this._observer = void 0;
  }
  handleEndThumbMouseDown(ev) {
    this.startThumbDrag("end", ev.clientX);
  }
  handleStartThumbMouseDown(ev) {
    this.startThumbDrag("start", ev.clientX);
  }
  getMinValue(thumbType) {
    return thumbType === "start" ? this.rangeMin : this.value[0] + this.step;
  }
  getMaxValue(thumbType) {
    return thumbType === "start" ? this.value[1] - this.step : this.rangeMax;
  }
  handleDocumentMouseMove(ev) {
    if (this._isDragging === false) {
      return;
    }
    const minValue = this.getMinValue(this._isDragging);
    const maxValue = this.getMaxValue(this._isDragging);
    const startValue = this._isDragging === "start" ? this.value[0] : this.value[1];
    const diffValue = (ev.clientX - this._dragStartX) / this.scale;
    const newValueNormalized = this.getNormalizedValue(startValue + diffValue, minValue, maxValue);
    if (this._isDragging === "start") {
      this._startValue = newValueNormalized;
    } else {
      this._endValue = newValueNormalized;
    }
  }
  handleDocumentMouseUp() {
    this.endThumbDrag();
  }
  handleDocumentMouseLeave() {
    this.endThumbDrag();
  }
  startThumbDrag(thumbType, startX) {
    this._isDragging = thumbType;
    this._dragStartX = startX;
    document.addEventListener("mousemove", this.handleDocumentMouseMove);
    document.addEventListener("mouseup", this.handleDocumentMouseUp);
    document.addEventListener("mouseleave", this.handleDocumentMouseLeave);
  }
  endThumbDrag() {
    document.removeEventListener("mousemove", this.handleDocumentMouseMove);
    document.removeEventListener("mouseup", this.handleDocumentMouseUp);
    document.removeEventListener("mouseleave", this.handleDocumentMouseLeave);
    this._isDragging = false;
    this.updateValue();
  }
  updateValue() {
    const oldValue = this.value;
    this.value = [this._startValue, this._endValue];
    if (oldValue[0] !== this.value[0] || oldValue[1] !== this.value[1]) {
      this.emit({ name: "input", payload: this.value });
    }
  }
  handleKeyDown(ev) {
    var _a;
    const key = ev.key;
    const focusedElement = (_a = this.shadowRoot) == null ? void 0 : _a.activeElement;
    if (!this.supportedKeys.includes(key) || !focusedElement) {
      return;
    }
    ev.preventDefault();
    const thumbType = focusedElement === this._startThumb ? "start" : "end";
    const minValue = this.getMinValue(thumbType);
    const maxValue = this.getMaxValue(thumbType);
    let newValue = thumbType === "start" ? this._startValue : this._endValue;
    if (key === "ArrowLeft" || key === "ArrowDown") {
      newValue = Math.max(minValue, newValue - this.step);
    } else if (key === "ArrowRight" || key === "ArrowUp") {
      newValue = Math.min(maxValue, newValue + this.step);
    }
    if (thumbType === "start") {
      this._startValue = newValue;
    } else {
      this._endValue = newValue;
    }
    this.updateValue();
  }
  handleTrackMouseDown(ev) {
    const target = ev.target;
    if (target === this._startThumb || target === this._endThumb) {
      return;
    }
    const trackOffset = Math.min(target.clientWidth - TRACK_PADDING, Math.max(TRACK_PADDING, ev.offsetX)) - TRACK_PADDING;
    const newValueNormalized = this.getNormalizedValue(
      this.rangeMin + trackOffset / this.scale,
      this.rangeMin,
      this.rangeMax
    );
    let thumbToMove = "end";
    if (this.dsf_mode === "single") {
      thumbToMove = "end";
    } else {
      const startValueDistance = Math.abs(this._startValue - newValueNormalized);
      const endValueDistance = Math.abs(this._endValue - newValueNormalized);
      thumbToMove = startValueDistance < endValueDistance ? "start" : "end";
    }
    const thumbMinValue = this.getMinValue(thumbToMove);
    const thumbMaxValue = this.getMaxValue(thumbToMove);
    if (newValueNormalized < thumbMinValue || newValueNormalized > thumbMaxValue) {
      return;
    }
    if (thumbToMove === "start") {
      this._startValue = newValueNormalized;
    } else {
      this._endValue = newValueNormalized;
    }
    this.updateValue();
  }
  getNormalizedValue(value, valueMin, valueMax) {
    const newValueMinMaxNormalized = Math.min(valueMax, Math.max(valueMin, value));
    return Math.round(newValueMinMaxNormalized / this.step) * this.step;
  }
  formatValueLabel(value) {
    return this.dsf_value_format ? this.dsf_value_format.replace("{value}", String(value)) : String(value);
  }
  render() {
    const scale = this.scale;
    const startOffset = (this._startValue - this.rangeMin) * scale;
    const endOffset = (this._endValue - this.rangeMin) * scale;
    this.style.setProperty("--dsf-range-start-offset", `${startOffset}`);
    this.style.setProperty("--dsf-range-end-offset", `${endOffset}`);
    return html`
      <div class="flex flex-auto flex-col gap-4">
        ${when(
      !!this.dsf_label,
      () => html`<label id="label" for="endThumb" class="dsf-text-xxs px-8 text-neutral-400"
              >${this.dsf_label}</label
            >`
    )}
        <div
          class="relative flex h-[var(--dsf-range-thumb-size)] flex-auto cursor-pointer items-center justify-center px-[var(--dsf-range-half-thumb-size)]"
          @mousedown=${this.handleTrackMouseDown}
        >
          <div class="pointer-events-none flex flex-auto">
            <div id="rangeTrack" class="h-4 flex-auto rounded-full bg-neutral-100"></div>
            <div class="range-selected absolute h-4 rounded-full bg-secondary-300"></div>
          </div>
          ${when(
      this.dsf_mode === "range",
      () => html` <dsf-tooltip
              ._dsf_open=${this._isDragging === "start"}
              dsf_type="secondary"
              class="range-thumb range-thumb-start absolute h-[var(--dsf-range-thumb-size)] w-[var(--dsf-range-thumb-size)]"
              @mousedown=${this.handleStartThumbMouseDown}
            >
              <div
                slot="anchor"
                id="startThumb"
                class=${getThumbClasses(this, "start")}
                tabindex="0"
                role="slider"
                aria-valuemin=${this.rangeMin}
                aria-valuemax=${this.rangeMax}
                aria-valuenow=${this._startValue}
                aria-labelledby="label"
              ></div>
              <output for="startThumb">${this.formatValueLabel(this._startValue)}</output>
            </dsf-tooltip>`
    )}
          <dsf-tooltip
            ._dsf_open=${this._isDragging === "end"}
            dsf_type="secondary"
            class="range-thumb range-thumb-end absolute h-[var(--dsf-range-thumb-size)] w-[var(--dsf-range-thumb-size)]"
            @mousedown=${this.handleEndThumbMouseDown}
          >
            <div
              slot="anchor"
              id="endThumb"
              class=${getThumbClasses(this, "end")}
              tabindex="0"
              role="slider"
              aria-valuemin=${this.rangeMin}
              aria-valuemax=${this.rangeMax}
              aria-valuenow=${this._endValue}
              aria-labelledby="label"
            ></div>
            <output for="endThumb">${this.formatValueLabel(this._endValue)}</output>
          </dsf-tooltip>
        </div>
        <div class="dsf-text-xxs flex flex-auto justify-between px-8 text-neutral-400">
          <span>${this.formatValueLabel(this._startValue)}</span>
          <span>${this.formatValueLabel(this._endValue)}</span>
        </div>
      </div>
    `;
  }
};
DsfRangeSlider.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        --dsf-range-thumb-size: calc(1.6 * var(--dsf-base-size));
        --dsf-range-half-thumb-size: calc(var(--dsf-range-thumb-size) / 2);
        --dsf-range-start-offset: 0;
        --dsf-range-end-offset: 0;
      }

      .range-thumb {
        top: 0;
        &.range-thumb-start {
          left: calc(var(--dsf-range-start-offset) * 1px);
        }
        &.range-thumb-end {
          left: calc(var(--dsf-range-end-offset) * 1px);
        }
      }

      .range-selected {
        left: calc(var(--dsf-range-start-offset) * 1px + calc(0.8 * var(--dsf-base-size)));
        width: calc((var(--dsf-range-end-offset) - var(--dsf-range-start-offset)) * 1px);
      }
    `
];
__decorateClass([
  prop({
    type: Array,
    reflect: true,
    converter: {
      fromAttribute: (attr) => {
        if (!attr) {
          return DEFAULT_VALUE;
        }
        const [start, end] = attr.split(",").map(Number);
        return [start, end];
      },
      toAttribute: (value) => {
        if (!value) {
          return "";
        }
        return `${value[0]},${value[1]}`;
      }
    }
  })
], DsfRangeSlider.prototype, "value", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfRangeSlider.prototype, "dsf_mode", 2);
__decorateClass([
  prop({ type: Number, reflect: true })
], DsfRangeSlider.prototype, "dsf_min", 2);
__decorateClass([
  prop({ type: Number, reflect: true })
], DsfRangeSlider.prototype, "dsf_max", 2);
__decorateClass([
  prop({ type: Number, reflect: true })
], DsfRangeSlider.prototype, "dsf_step", 2);
__decorateClass([
  prop({ type: String })
], DsfRangeSlider.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfRangeSlider.prototype, "dsf_value_format", 2);
__decorateClass([
  query("#rangeTrack")
], DsfRangeSlider.prototype, "_rangeTrack", 2);
__decorateClass([
  query("#startThumb")
], DsfRangeSlider.prototype, "_startThumb", 2);
__decorateClass([
  query("#endThumb")
], DsfRangeSlider.prototype, "_endThumb", 2);
__decorateClass([
  state()
], DsfRangeSlider.prototype, "_startValue", 2);
__decorateClass([
  state()
], DsfRangeSlider.prototype, "_endValue", 2);
__decorateClass([
  state()
], DsfRangeSlider.prototype, "_isDragging", 2);
DsfRangeSlider = __decorateClass([
  defineElement("dsf-range-slider")
], DsfRangeSlider);
export {
  DsfRangeSlider
};
